import {
  CheckCircle,
  Trash,
  EnvelopeSlash,
  Check2All,
  EnvelopeCheck,
  CheckAll,
} from "react-bootstrap-icons";
import React, { useEffect, useRef, useState } from "react";
import { ListGroup, Badge } from "react-bootstrap";
import { ExclamationCircle } from "react-bootstrap-icons";
import { Chat, useChatStore } from "@/entities/chat";
import { Contact } from "@/entities/contact";
import { formatDate, getLastMessagePreview } from "@/shared/lib";
import "./chat-list-item.css";
import { DeleteConfirmAction } from "@/shared/ui";
import { useAuthStore } from "@/features/auth";
import { sendWSMessage } from "@/processes/socket";

interface ChatListItemProps {
  chat: Chat;
  userId: string | null;
  selectedChats: string[];
  hasUnread: boolean;
  hasRead: boolean;
  currentChatId: string | undefined;
  onSelectChat: (chat: Chat) => void;
  onSelectChats: (chatIds: string) => void;
  getCurrentChatName: (contact: Contact | undefined) => string;
}

export const ChatListItem = ({
  chat,
  selectedChats,
  hasUnread,
  hasRead,
  currentChatId,
  onSelectChat,
  getCurrentChatName,
}: ChatListItemProps) => {
  const contact = chat.contact;
  const chatName = getCurrentChatName(contact);
  const err = chat.lastMessage?.err;
  const toggleSelectChats = useChatStore((state) => state.toggleSelectChats);
  const clearSelectedChats = useChatStore((state) => state.clearSelectedChats);
  const setSelectedAllChats = useChatStore((state) => state.setSelectedAllChats);
  const deleteChats = useChatStore((state) => state.deleteChats);
  const asUnread = chat?.isReaded === false;

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const contextRef = useRef<HTMLDivElement | null>(null);

  const isSelected = selectedChats.includes(chat._id);

  const selectedMode = selectedChats.length > 0;
  const shouldMarkAsRead = asUnread || chat.unreadedMessageCount > 0;

  const lastMessageTime = chat.draftMessage?.createdAt
    ? formatDate(chat.draftMessage.createdAt)
    : chat.lastMessage?.createdAt
      ? formatDate(chat.lastMessage.createdAt)
      : "";

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (contextRef.current && !contextRef.current.contains(target)) {
        setContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY });
  };

  const { user } = useAuthStore();


  const handleMarkReadDynamic = async () => {
    setContextMenu(null);

    if (selectedMode) {
      // Обрабатываем все выделенные чаты
      const store = useChatStore.getState();
      const chats = store.chats.filter(c => selectedChats.includes(c._id));

      // Сбор чатов, у которых нужно отправить sendReadedMessages
      const chatsWithUnreadCount = chats.filter(c => c.unreadedMessageCount > 0);
      const chatsWithAsUnread = chats.filter(c => c.isReaded === false);

      // 1. Обновляем статус asUnread для всех подходящих чатов
      if (chatsWithAsUnread.length > 0) {
        store.updateReadStatus(chatsWithAsUnread.map(c => c._id), false);
      }

      // 2. Отправляем подтверждение прочтения для чатов с unreadedMessageCount
      if (user?._id && chatsWithUnreadCount.length > 0) {
        for (const c of chatsWithUnreadCount) {
          if (c.lastMessage?._id) {
            if (c.lastMessage._id && user) {
              sendWSMessage("message:readed", {
                chat: c._id,
                message: c.lastMessage._id,
                time: Date.now(),
              });
            }
          }
        }
      }

      clearSelectedChats();
    } else {
      if (asUnread && user?._id) {
        useChatStore.getState().updateReadStatus([chat._id], true);
      }

      if (chat.unreadedMessageCount > 0 && user?._id && chat.lastMessage?._id) {
        sendWSMessage("message:readed", {
          chat: chat._id,
          message: chat.lastMessage._id,
          time: Date.now(),
        });
      }
    }
  };

  const handleMarkUnreadDynamic = () => {
    setContextMenu(null);
    if (selectedMode) {
      useChatStore.getState().updateReadStatus(selectedChats, false);
      clearSelectedChats();
    } else {
      useChatStore.getState().updateReadStatus([chat._id], false);
    }
  };
  const handleSelectToggle = () => {
    toggleSelectChats(chat._id);
    setContextMenu(null);
  };

  const handleClick = () => {
    if (selectedMode) toggleSelectChats(chat._id);
    else onSelectChat(chat);
  };

  return (
    <>
      <ListGroup.Item
        key={chat._id}
        onContextMenu={handleContextMenu}
        onClick={handleClick}
        variant="secondary"
        className="d-flex align-items-center rounded-5 mb-1 chat-item"
        style={{
          userSelect: selectedMode ? "none" : "auto",
          cursor: "pointer",
          backgroundColor: isSelected
            ? "#9ad0ff"
            : chat._id === currentChatId
              ? "#bebebe"
              : "",
          position: "relative",
        }}
      >
        <div className="flex-shrink-0 position-relative me-2" style={{ width: 40 }}>
          <div
            className="d-flex justify-content-center align-items-center rounded-circle bg-secondary text-white"
            style={{ width: 40, height: 40, fontWeight: "bold", fontSize: "1rem" }}
          >
            {contact?.firstName?.[0] ?? "?"}
          </div>
          {contact?.user.isOnline && (
            <div
              className="position-absolute rounded-circle border border-white"
              style={{
                width: 10,
                height: 10,
                bottom: 0,
                right: 0,
                backgroundColor: "#12ea12",
              }}
            />
          )}
        </div>

        <div className="d-flex align-items-center flex-grow-1 overflow-hidden min-w-0">
          <div className="d-flex flex-column flex-grow-1 overflow-hidden min-w-0">
            <span className="fw-semibold text-truncate">{chatName}</span>
            <span
              className="d-flex small text-muted text-truncate"
              style={{ lineHeight: "16px", alignItems: "center" }}
            >
              {err && (
                <div
                  style={{
                    marginTop: 2,
                    marginRight: 2,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#dc3545",
                    color: "white",
                    fontSize: "10px",
                    zIndex: 1,
                  }}
                >
                  <ExclamationCircle size={4} />
                </div>
              )}
              {chat?.draftMessage ? (
                <>
                  <strong>Черновик: </strong>
                  {chat.draftMessage.text}
                </>
              ) : (
                getLastMessagePreview(chat?.lastMessage) || ""
              )}
            </span>
          </div>

          <div
            style={{ height: "40px" }}
            className="d-flex flex-column justify-content-between align-items-end overflow-hidden flex-shrink-0 ms-2 me-1 min-w-0"
          >
            <small
              className="text-muted text-truncate"
              style={{
                fontSize: "0.7rem",
                paddingTop: 6,
                lineHeight: "16px",
                maxWidth: "100%",
              }}
            >
              {lastMessageTime}
            </small>

            {chat.unreadedMessageCount > 0 && (
              <Badge
                pill
                bg="secondary"
                style={{
                  fontSize: "0.5rem",
                  fontWeight: 400,
                  minWidth: 15,
                  height: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {chat.unreadedMessageCount}
              </Badge>
            )}
            {asUnread && chat.unreadedMessageCount < 1 && (
              <Badge
                pill
                bg="secondary"
                style={{
                  fontSize: "0.5rem",
                  fontWeight: 400,
                  minWidth: 15,
                  height: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {""}
              </Badge>
            )}
          </div>
        </div>
      </ListGroup.Item>

      {contextMenu && (
        <div
          ref={contextRef}
          className="chat-context-menu"
          style={{
            position: "absolute",
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          <div
            onClick={handleSelectToggle}
            className="dropdown-item d-flex align-items-center"
          >
            <CheckCircle className="me-2" /> {isSelected ? "Снять выделение" : "Выделить"}
          </div>

          {selectedMode && selectedChats.length > 1 && (
            <div
              onClick={() => {
                clearSelectedChats();
                setContextMenu(null);
              }}
              className="dropdown-item d-flex align-items-center"
            >
              <CheckAll className="me-2" /> Снять выделение со всех
            </div>
          )}

          {/* {selectedMode && ( */}
          <div
            onClick={() => {
              setSelectedAllChats();
              setContextMenu(null);
            }}
            className="dropdown-item d-flex align-items-center"
          >
            <Check2All className="me-2" /> Выделить все
          </div>
          {/* )}  */}
          {!selectedMode && (
            <div
              onClick={shouldMarkAsRead ? handleMarkReadDynamic : handleMarkUnreadDynamic}
              className="dropdown-item d-flex align-items-center"
            >
              {shouldMarkAsRead ? (
                <>
                  <EnvelopeCheck className="me-2" /> Сделать прочитанным
                </>
              ) : (
                <>
                  <EnvelopeSlash className="me-2" /> Сделать непрочитанным
                </>
              )}
            </div>
          )}

          {selectedMode && (
            <>
              {
                hasRead &&
                <div
                  onClick={handleMarkUnreadDynamic}
                  className="dropdown-item d-flex align-items-center"
                >
                  <EnvelopeSlash className="me-2" /> Пометить как непрочитанные
                </div>
              }
              {
                hasUnread &&
                <div
                  onClick={handleMarkReadDynamic}
                  className="dropdown-item d-flex align-items-center"
                >
                  <EnvelopeCheck className="me-2" /> Пометить как прочитанные
                </div>
              }
            </>
          )}

          {/* <div
            onClick={handleMarkUnread}
            className="dropdown-item d-flex align-items-center"
          >
            <EnvelopeSlash className="me-2" /> Пометить как непрочитанное
          </div>

          <div
            onClick={handleMarkRead}
            className="dropdown-item d-flex align-items-center"
          >
            <EnvelopeCheck className="me-2" /> Пометить как прочитанное
          </div> */}

          <DeleteConfirmAction
            triggerElement={
              <div className="dropdown-item d-flex align-items-center text-danger">
                <Trash className="me-2" />
                {selectedMode ? "Удалить чаты" : "Удалить чат"}
              </div>
            }
            confirmText={
              selectedMode
                ? "Удалить выбранные чаты?"
                : <>Удалить чат <strong>{chatName}</strong>?</>
            }
            onConfirm={() => {
              if (selectedMode) deleteChats(selectedChats);
              else deleteChats([chat._id]);
              clearSelectedChats();
            }}
            onCloseMenu={() => setContextMenu(null)}
          />
        </div>
      )}
    </>
  );
};
